import React, { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { athleteAddCustomWorkout } from "../../lib/api";

const TYPE_OPTIONS = [
  { value: "cross", label: "Other Sport" },
  { value: "strength", label: "Strength" },
  { value: "mobility", label: "Mobility" },
  { value: "easy", label: "Run" },
  { value: "other", label: "Other" },
];

/**
 * AddCustomWorkoutModal — lets an athlete drop their own extra session
 * (ball hockey, a lift, yoga…) onto a day of their program. Lightweight on
 * purpose: type + name, optional time and note. Shows up for the coach too.
 */
export default function AddCustomWorkoutModal({ programId, weekNumber, dayLabel, dayName, onClose, onAdded }) {
  const [type, setType] = useState("cross");
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || saving) return;
    setSaving(true);
    try {
      await athleteAddCustomWorkout({
        program_id: programId,
        week_number: weekNumber,
        day_label: dayLabel,
        type,
        title: title.trim(),
        duration_or_distance: duration.trim() || null,
        notes: notes.trim() || null,
      });
      toast.success("Added to your week — your coach can see it too");
      onAdded?.();
    } catch {
      toast.error("Couldn't add — please try again");
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-0 sm:p-4"
      data-testid="add-custom-workout-modal"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-white border border-[var(--tir-ink)] shadow-2xl rounded-t-lg sm:rounded-none max-h-[88vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between p-4 sm:p-5 border-b border-[var(--tir-border)]">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-navy)]">
              {dayName || dayLabel} · Week {weekNumber}
            </div>
            <h3 className="font-serif-display text-xl text-[var(--tir-ink)] leading-tight">
              Add your own workout
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[var(--tir-muted)] hover:text-[var(--tir-ink)] p-1"
            aria-label="Close"
            data-testid="add-custom-close"
          >
            <X size={18} />
          </button>
        </div>
        <div className="p-4 sm:p-5 space-y-4">
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1.5">
              What kind?
            </label>
            <div className="flex flex-wrap gap-1.5">
              {TYPE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => setType(o.value)}
                  data-testid={`custom-type-${o.value}`}
                  className={`px-3 py-1.5 text-[11px] tracking-[0.12em] uppercase font-semibold rounded-full border transition-colors ${
                    type === o.value
                      ? "bg-[var(--tir-navy)] text-white border-[var(--tir-navy)]"
                      : "bg-white text-[var(--tir-muted)] border-[var(--tir-border)] hover:border-[var(--tir-navy)]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Name it
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Ball hockey, Upper body lift…"
              autoFocus
              data-testid="custom-title-input"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              How long / far? (optional)
            </label>
            <input
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
              placeholder="e.g. 60 min"
              data-testid="custom-duration-input"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Anything worth remembering?"
              data-testid="custom-notes-input"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-navy)] px-3 py-2 text-sm focus:outline-none resize-y"
            />
          </div>
        </div>
        <div className="p-4 sm:p-5 border-t border-[var(--tir-border)] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] hover:text-[var(--tir-ink)]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!title.trim() || saving}
            data-testid="custom-save-btn"
            className="px-5 py-2 text-[10px] tracking-[0.22em] uppercase font-bold bg-[var(--tir-navy)] text-white hover:bg-[#152a45] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
          >
            {saving ? "Adding…" : "Add workout"}
          </button>
        </div>
      </form>
    </div>
  );
}
